import { FormlyFieldConfig } from '@ngx-formly/core';
import { Application } from '@intellio/shared/models';
import { Observable, take, takeUntil } from 'rxjs';
import type { FormlyHookFn } from './formly-hook-fn.type';

const normalizeEnergySourceCombination = (value: unknown): string[] | null => {
  if (!Array.isArray(value)) {
    return null;
  }

  const normalized = value
    .filter((energySource): energySource is string => typeof energySource === 'string')
    .map((energySource) => energySource.trim().toLowerCase())
    .filter(Boolean);

  if (normalized.length !== value.length || normalized.length === 0) {
    return null;
  }

  return Array.from(new Set(normalized)).sort();
};

export const hasExactEnergySourceCombination = (
  energySourceIds: unknown,
  allowedCombinations: unknown
): boolean => {
  const energySources = normalizeEnergySourceCombination(energySourceIds);

  if (!energySources || !Array.isArray(allowedCombinations)) {
    return false;
  }

  return allowedCombinations.some((allowedCombination) => {
    const allowedEnergySources = normalizeEnergySourceCombination(
      allowedCombination
    );

    return (
      !!allowedEnergySources &&
      energySources.length === allowedEnergySources.length &&
      energySources.every(
        (energySource, index) => energySource === allowedEnergySources[index]
      )
    );
  });
};

export const energySourceCombinationEligibility = (
  currentApplication$: Observable<Application>,
  destroyed$: Observable<unknown>
): FormlyHookFn => {
  return (field: FormlyFieldConfig) => {
    currentApplication$
      .pipe(take(1), takeUntil(destroyed$))
      .subscribe((application) => {
        field.formControl?.setValue(
          hasExactEnergySourceCombination(
            application.energySourceIds ?? application.system.energySourceIds,
            field.props?.['allowedEnergySourceCombinations']
          )
        );
      });
  };
};
